import React, { useState, useEffect, useCallback } from 'react';
import {
    View,
    Text,
    FlatList,
    StyleSheet,
    TouchableOpacity,
    Alert,
    RefreshControl,
    ActivityIndicator,
    SafeAreaView,
    Platform,
    Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getMyOrders } from '../../api/marketplace';
import { getShadow } from '../../theme/useTheme';
import MarketplaceFooter from '../../components/common/MarketplaceFooter';

const OrderHistoryScreen = ({ navigation }) => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [activeTab, setActiveTab] = useState('ongoing'); // 'ongoing' or 'canceled'

    const fetchOrders = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const data = await getMyOrders();
            setOrders(data);
        } catch (error) {
            console.error('Fetch Orders Error:', error);
            if (showLoading) {
                Alert.alert('Unable to load orders', 'Please check your connection or try again later.');
            }
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleRefresh = () => {
        setRefreshing(true);
        fetchOrders(false);
    };

    const ongoingStatuses = ['pending_payment', 'paid_processing', 'dispatched', 'delivered', 'completed'];
    const canceledStatuses = ['cancelled', 'disputed'];

    const filteredOrders = orders.filter(order => {
        if (activeTab === 'ongoing') {
            return ongoingStatuses.includes(order.status);
        } else {
            return canceledStatuses.includes(order.status);
        }
    });

    const getStatusConfig = (status) => {
        switch (status) {
            case 'completed':
            case 'delivered':
                return { label: 'DELIVERED', color: '#FFF', bg: '#10B981' };
            case 'pending_payment':
                return { label: 'PENDING PAYMENT', color: '#FFF', bg: '#F59E0B' };
            case 'paid_processing':
                return { label: 'PROCESSING', color: '#FFF', bg: '#6366F1' };
            case 'dispatched':
                return { label: 'DISPATCHED', color: '#FFF', bg: '#3B82F6' };
            case 'cancelled':
                return { label: 'CANCELLED', color: '#FFF', bg: '#EF4444' };
            case 'disputed':
                return { label: 'DISPUTED', color: '#FFF', bg: '#9CA3AF' };
            default:
                return { label: status?.toUpperCase()?.replace('_', ' ') || 'PENDING', color: '#FFF', bg: '#6B7280' };
        }
    };

    const renderOrder = ({ item }) => {
        const status = getStatusConfig(item.status);
        const firstItem = item.items[0]?.item;
        const itemCount = item.items.length;
        const date = new Date(item.createdAt).toLocaleDateString();

        return (
            <TouchableOpacity
                style={styles.orderCard}
                onPress={() => navigation.navigate('OrderDetail', { orderId: item.id })}
                activeOpacity={0.8}
            >
                <View style={styles.cardMain}>
                    <Image
                        source={{ uri: firstItem?.images?.[0] || 'https://via.placeholder.com/100' }}
                        style={styles.productImage}
                    />
                    <View style={styles.orderInfo}>
                        <Text style={styles.productName} numberOfLines={1}>
                            {firstItem?.name || 'Order Items'}
                            {itemCount > 1 ? ` + ${itemCount - 1} more` : ''}
                        </Text>
                        <Text style={styles.orderNumber}>Order #{item.id}</Text>

                        <View style={[styles.statusBadge, { backgroundColor: status.bg }]}>
                            <Text style={styles.statusText}>{status.label}</Text>
                        </View>

                        <Text style={styles.orderDate}>On {date}</Text>
                    </View>
                    <Ionicons name="chevron-forward" size={16} color="#D1D5DB" />
                </View>
            </TouchableOpacity>
        );
    };

    return (
        <View style={styles.container}>
            {/* Header */}
            <View style={styles.screenHeader}>
                <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
                    <Ionicons name="chevron-back" size={20} color="#1F2937" />
                </TouchableOpacity>
                <View style={styles.searchBar}>
                    <Ionicons name="search" size={16} color="#94A3B8" style={{ marginRight: 8 }} />
                    <Text style={styles.searchText}>Search Orders</Text>
                </View>
                <TouchableOpacity style={styles.cartButton} onPress={() => navigation.navigate('Cart')}>
                    <Ionicons name="cart-outline" size={22} color="#1F2937" />
                </TouchableOpacity>
            </View>

            <View style={styles.titleRow}>
                <Text style={styles.screenTitle}>My Orders</Text>
            </View>

            {/* Tabs */}
            <View style={styles.tabsContainer}>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'ongoing' && styles.activeTab]}
                    onPress={() => setActiveTab('ongoing')}
                >
                    <Text style={[styles.tabText, activeTab === 'ongoing' && styles.activeTabText]}>
                        ACTIVE
                    </Text>
                </TouchableOpacity>
                <TouchableOpacity
                    style={[styles.tab, activeTab === 'canceled' && styles.activeTab]}
                    onPress={() => setActiveTab('canceled')}
                >
                    <Text style={[styles.tabText, activeTab === 'canceled' && styles.activeTabText]}>
                        CANCELLED
                    </Text>
                </TouchableOpacity>
            </View>

            {loading && !refreshing ? (
                <View style={styles.centerContainer}>
                    <ActivityIndicator size="large" color="#F59E0B" />
                </View>
            ) : (
                <FlatList
                    data={filteredOrders}
                    renderItem={renderOrder}
                    keyExtractor={item => item.id.toString()}
                    refreshControl={
                        <RefreshControl
                            refreshing={refreshing}
                            onRefresh={handleRefresh}
                            colors={['#F59E0B']}
                            tintColor="#F59E0B"
                        />
                    }
                    contentContainerStyle={styles.listContent}
                    ListEmptyComponent={
                        <View style={styles.emptyContainer}>
                            <View style={styles.emptyIconCircle}>
                                <Ionicons name="basket-outline" size={32} color="#CBD5E1" />
                            </View>
                            <Text style={styles.emptyTitle}>No orders</Text>
                            <Text style={styles.emptySubtitle}>You have no {activeTab} orders at the moment.</Text>
                            <TouchableOpacity
                                style={styles.browseButton}
                                onPress={() => navigation.navigate('Marketplace')}
                            >
                                <Text style={styles.browseButtonText}>Go to Marketplace</Text>
                            </TouchableOpacity>
                        </View>
                    }
                />
            )}
            <MarketplaceFooter activeTab="Profile" navigation={navigation} />
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#F9FAFB', // Uniform off-white background
    },
    centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    screenHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        backgroundColor: '#FFF',
        paddingTop: Platform.OS === 'ios' ? 50 : 40,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    backButton: {
        marginRight: 10,
    },
    searchBar: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F1F5F9',
        borderRadius: 8,
        paddingHorizontal: 10,
        height: 32,
    },
    searchText: {
        color: '#94A3B8',
        fontSize: 12,
    },
    cartButton: {
        marginLeft: 10,
    },
    titleRow: {
        backgroundColor: '#FFF',
        paddingHorizontal: 16,
        paddingBottom: 8,
        paddingTop: 10,
    },
    screenTitle: {
        fontSize: 20,
        fontWeight: '900',
        color: '#111827',
    },
    tabsContainer: {
        flexDirection: 'row',
        backgroundColor: '#FFF',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
    },
    tab: {
        flex: 1,
        paddingVertical: 12,
        alignItems: 'center',
        borderBottomWidth: 3,
        borderBottomColor: 'transparent',
    },
    activeTab: {
        borderBottomColor: '#F59E0B',
    },
    tabText: {
        fontSize: 12,
        fontWeight: '800',
        color: '#94A3B8',
    },
    activeTabText: {
        color: '#F59E0B',
    },
    listContent: {
        paddingBottom: 20,
        paddingTop: 10,
    },
    orderCard: {
        backgroundColor: '#FFF',
        marginBottom: 8,
        marginHorizontal: 16,
        borderRadius: 16,
        padding: 12,
        borderWidth: 1,
        borderColor: '#F1F5F9',
        ...getShadow('xs'),
    },
    cardMain: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    productImage: {
        width: 60,
        height: 60,
        borderRadius: 10,
        backgroundColor: '#F8FAFB',
    },
    orderInfo: {
        flex: 1,
        marginLeft: 12,
    },
    productName: {
        fontSize: 14,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 1,
    },
    orderNumber: {
        fontSize: 11,
        color: '#64748B',
        marginBottom: 4,
    },
    statusBadge: {
        alignSelf: 'flex-start',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6,
        marginBottom: 4,
    },
    statusText: {
        fontSize: 10,
        fontWeight: '900',
        color: '#FFF',
    },
    orderDate: {
        fontSize: 11,
        color: '#94A3B8',
    },
    emptyContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 80,
    },
    emptyIconCircle: {
        width: 64,
        height: 64,
        borderRadius: 32,
        backgroundColor: '#FFF',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: 16,
        borderWidth: 1,
        borderColor: '#F1F5F9',
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '900',
        color: '#1E293B',
        marginBottom: 6,
    },
    emptySubtitle: {
        fontSize: 13,
        color: '#94A3B8',
        textAlign: 'center',
        paddingHorizontal: 40,
        marginBottom: 20,
    },
    browseButton: {
        backgroundColor: '#111827',
        paddingHorizontal: 20,
        paddingVertical: 10,
        borderRadius: 12,
    },
    browseButtonText: {
        color: '#FFF',
        fontWeight: '800',
        fontSize: 13,
    },
});

export default OrderHistoryScreen;
